/**
 * <sticky-atc>
 *
 * A sticky Add to Cart bar for the product page. It does not own any variant
 * logic of its own — instead it mirrors the main product form:
 *   - its swatch / dropdown controls proxy the selection to the main variant
 *     picker, so all availability + variant resolution stays in one place
 *   - its Add to Cart button submits the main product form (form="…")
 *   - price, availability and selected options stay in sync via observers
 */
if (!customElements.get("sticky-atc")) {
	customElements.define(
		"sticky-atc",
		class StickyAtc extends HTMLElement {
			connectedCallback() {
				this.sectionId = this.dataset.section;

				this.mainPicker = document.querySelector(
					`variant-radios[data-section="${this.sectionId}"], variant-selects[data-section="${this.sectionId}"]`
				);
				this.mainSubmit = document.getElementById(
					`ProductSubmitButton-${this.sectionId}`
				);
				this.mainPrice = document.getElementById(`price-${this.sectionId}`);

				this.stickyPrice = document.getElementById(`StickyPrice-${this.sectionId}`);
				this.stickyAdd = document.getElementById(`StickyAddButton-${this.sectionId}`);
				this.stickySelect = document.getElementById(`StickySelect-${this.sectionId}`);
				this.thumb = document.getElementById(`StickyThumb-${this.sectionId}`);
				this.proxyControls = Array.from(
					this.querySelectorAll("[data-main-input]")
				);
				this.dropdowns = Array.from(
					this.querySelectorAll("[data-sticky-dropdown]")
				);

				this.setupVisibilityObserver();
				this.setupVariantSync();
				this.setupSwatches();
				this.setupDropdowns();
				this.setupMobileSelect();
				this.setupRewardsOffset();

				// Reflect whatever is selected on first paint.
				this.syncControls();
			}

			disconnectedCallback() {
				this.visibilityObserver?.disconnect();
				this.priceObserver?.disconnect();
				this.buttonObserver?.disconnect();
				this.rewardsResizeObserver?.disconnect();
				document.removeEventListener("click", this.onDocumentClick);
				document.body.classList.remove("sticky-atc-visible");
				if (this.applyRewardsOffset) {
					window.removeEventListener("resize", this.applyRewardsOffset);
				}
			}

			/* ---- Visibility ------------------------------------------------ */

			setupVisibilityObserver() {
				const anchor = this.mainSubmit
					? this.mainSubmit.closest(".product-form__buttons") || this.mainSubmit
					: null;

				if (!anchor || !("IntersectionObserver" in window)) {
					this.setVisible(true);
					return;
				}

				this.visibilityObserver = new IntersectionObserver(
					(entries) => {
						entries.forEach((entry) => {
							// Reveal only once the main Add to Cart has scrolled up and
							// out of the viewport (i.e. the shopper has moved past it).
							const scrolledPast =
								!entry.isIntersecting && entry.boundingClientRect.top < 0;
							this.setVisible(scrolledPast);
						});
					},
					{ threshold: 0 }
				);
				this.visibilityObserver.observe(anchor);
			}

			setVisible(visible) {
				this.classList.toggle("is-visible", visible);
				this.setAttribute("aria-hidden", visible ? "false" : "true");
				// Hide the Gorgias chat launcher while the bar is showing.
				document.body.classList.toggle("sticky-atc-visible", visible);
				if (!visible) this.dropdowns.forEach((dd) => this.closeDropdown(dd));
			}

			/* ---- Sync with the main form ----------------------------------- */

			setupVariantSync() {
				if (this.mainPicker) {
					this.mainPicker.addEventListener("change", () => this.syncFromMain());
				}

				// Price is re-rendered asynchronously (section fetch) into the main
				// price block, so mirror it whenever its contents change.
				if (this.mainPrice && this.stickyPrice && "MutationObserver" in window) {
					this.priceObserver = new MutationObserver(() => this.syncPrice());
					this.priceObserver.observe(this.mainPrice, {
						childList: true,
						subtree: true,
					});
				}

				// The main Add to Cart button toggles disabled / text as the variant
				// changes — mirror its state onto the sticky button.
				if (this.mainSubmit && this.stickyAdd && "MutationObserver" in window) {
					this.buttonObserver = new MutationObserver(() => this.syncButton());
					this.buttonObserver.observe(this.mainSubmit, {
						attributes: true,
						childList: true,
						subtree: true,
						attributeFilter: ["disabled", "class"],
					});
				}
			}

			syncFromMain() {
				this.syncControls();
				this.syncButton();
				this.syncPrice();
				this.syncThumb();
			}

			syncControls() {
				this.proxyControls.forEach((ctrl) => {
					const input = document.getElementById(ctrl.dataset.mainInput);
					if (!input) return;

					const selected = input.checked;
					const disabled = input.classList.contains("disabled");

					ctrl.classList.toggle("is-selected", selected);
					ctrl.classList.toggle("is-disabled", disabled && !selected);

					if (ctrl.getAttribute("role") === "option") {
						ctrl.setAttribute("aria-selected", selected ? "true" : "false");
						ctrl.toggleAttribute("aria-disabled", disabled && !selected);
					} else {
						ctrl.setAttribute("aria-pressed", selected ? "true" : "false");
						ctrl.toggleAttribute("disabled", disabled && !selected);
					}
				});

				this.updateDropdownValues();
			}

			updateDropdownValues() {
				this.dropdowns.forEach((dd) => {
					const selected = dd.querySelector(".sticky-atc__dropdown-option.is-selected");
					const valueEl = dd.querySelector(".sticky-atc__dropdown-value");
					if (selected && valueEl) valueEl.textContent = selected.dataset.value;
				});
			}

			syncPrice() {
				if (!this.mainPrice || !this.stickyPrice) return;
			  
				this.stickyPrice.innerHTML = this.mainPrice.innerHTML;
				this.stickyPrice.querySelector(".card__badge")?.remove();
			  }

			syncButton() {
				if (!this.stickyAdd || !this.mainSubmit) return;

				const disabled = this.mainSubmit.hasAttribute("disabled");
				this.stickyAdd.toggleAttribute("disabled", disabled);
				this.classList.toggle("is-sold-out", disabled);

				const label = this.stickyAdd.querySelector(".sticky-atc__add-text");
				if (label && window.variantStrings) {
					label.textContent = disabled
						? window.variantStrings.soldOut
						: window.variantStrings.addToCart;
				}
			}

			syncThumb() {
				if (!this.thumb || !this.mainPicker) return;
				const variant = this.mainPicker.currentVariant;
				const src = variant?.featured_image?.src;
				if (src) {
					this.thumb.src = src.includes("?")
						? `${src}&width=120`
						: `${src}?width=120`;
				}
			}

			/* ---- Selection proxy ------------------------------------------- */

			// Selecting the matching input in the main picker triggers the theme's
			// own variant-change flow, which then flows back to us via syncFromMain.
			selectMainInput(id) {
				const input = document.getElementById(id);
				if (!input || input.classList.contains("disabled")) return false;
				if (!input.checked) {
					input.checked = true;
					input.dispatchEvent(new Event("change", { bubbles: true }));
				}
				return true;
			}

			setupSwatches() {
				this.querySelectorAll(
					".sticky-atc__swatch[data-main-input], .sticky-atc__pill[data-main-input]"
				).forEach((control) => {
					control.addEventListener("click", (event) => {
						event.preventDefault();
						this.selectMainInput(control.dataset.mainInput);
					});
				});
			}

			/* ---- Custom dropdown ------------------------------------------- */

			setupDropdowns() {
				this.dropdowns.forEach((dd) => {
					const toggle = dd.querySelector(".sticky-atc__dropdown-toggle");
					const list = dd.querySelector(".sticky-atc__dropdown-list");
					if (!toggle || !list) return;

					toggle.addEventListener("click", (event) => {
						event.stopPropagation();
						const open = dd.classList.contains("is-open");
						this.dropdowns.forEach((other) => {
							if (other !== dd) this.closeDropdown(other);
						});
						open ? this.closeDropdown(dd) : this.openDropdown(dd);
					});

					list.querySelectorAll(".sticky-atc__dropdown-option").forEach(
						(option) => {
							option.addEventListener("click", () => {
								if (option.classList.contains("is-disabled")) return;
								this.selectMainInput(option.dataset.mainInput);
								this.closeDropdown(dd);
								toggle.focus();
							});
						}
					);

					list.addEventListener("keydown", (event) =>
						this.onDropdownKeydown(event, dd)
					);
					toggle.addEventListener("keydown", (event) => {
						if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
							event.preventDefault();
							this.openDropdown(dd);
						}
					});
				});

				// Close any open dropdown when clicking outside the bar.
				this.onDocumentClick = (event) => {
					if (!this.contains(event.target)) {
						this.dropdowns.forEach((dd) => this.closeDropdown(dd));
					}
				};
				document.addEventListener("click", this.onDocumentClick);
			}

			openDropdown(dd) {
				const list = dd.querySelector(".sticky-atc__dropdown-list");
				const toggle = dd.querySelector(".sticky-atc__dropdown-toggle");
				dd.classList.add("is-open");
				toggle.setAttribute("aria-expanded", "true");
				list.hidden = false;
				list.focus({ preventScroll: true });
				const focusTarget =
					list.querySelector(".sticky-atc__dropdown-option.is-selected") ||
					list.querySelector(".sticky-atc__dropdown-option:not(.is-disabled)");
				if (focusTarget) this.focusOption(focusTarget);
			}

			closeDropdown(dd) {
				const list = dd.querySelector(".sticky-atc__dropdown-list");
				const toggle = dd.querySelector(".sticky-atc__dropdown-toggle");
				if (!dd.classList.contains("is-open")) return;
				dd.classList.remove("is-open");
				toggle.setAttribute("aria-expanded", "false");
				list.hidden = true;
			}

			focusOption(option) {
				const list = option.closest(".sticky-atc__dropdown-list");
				list
					.querySelectorAll(".sticky-atc__dropdown-option")
					.forEach((o) => o.classList.remove("is-active"));
				option.classList.add("is-active");
				if (option.id) list.setAttribute("aria-activedescendant", option.id);
				option.scrollIntoView({ block: "nearest" });
			}

			onDropdownKeydown(event, dd) {
				const list = dd.querySelector(".sticky-atc__dropdown-list");
				const options = Array.from(
					list.querySelectorAll(".sticky-atc__dropdown-option:not(.is-disabled)")
				);
				const current = list.querySelector(".sticky-atc__dropdown-option.is-active");
				let index = options.indexOf(current);

				switch (event.key) {
					case "ArrowDown":
						event.preventDefault();
						index = Math.min(index + 1, options.length - 1);
						if (options[index]) this.focusOption(options[index]);
						break;
					case "ArrowUp":
						event.preventDefault();
						index = index <= 0 ? 0 : index - 1;
						if (options[index]) this.focusOption(options[index]);
						break;
					case "Home":
						event.preventDefault();
						if (options[0]) this.focusOption(options[0]);
						break;
					case "End":
						event.preventDefault();
						if (options[options.length - 1])
							this.focusOption(options[options.length - 1]);
						break;
					case "Enter":
					case " ":
						event.preventDefault();
						if (current) {
							this.selectMainInput(current.dataset.mainInput);
							this.closeDropdown(dd);
							dd.querySelector(".sticky-atc__dropdown-toggle").focus();
						}
						break;
					case "Escape":
						event.preventDefault();
						this.closeDropdown(dd);
						dd.querySelector(".sticky-atc__dropdown-toggle").focus();
						break;
				}
			}

			/* ---- Sit above the Yotpo rewards banner ------------------------ */

			// The Yotpo rewards widget renders its own bottom-fixed banner (mostly
			// on mobile). Offset the sticky bar up by that banner's height so the
			// two never overlap. Yotpo loads asynchronously and the banner can
			// resize, so we poll until it appears and then observe it.
			setupRewardsOffset() {
				this.applyRewardsOffset = () => {
					const banner = document.querySelector(
						".yotpo-floater-widget-layout .yotpo-banner-layout"
					);
					let offset = 0;

					if (banner) {
						const rect = banner.getBoundingClientRect();
						// Only clear it when the banner is actually pinned to the bottom
						// of the viewport (skip desktop side-tab layouts).
						const pinnedToBottom =
							rect.width > 0 &&
							Math.abs(rect.bottom - window.innerHeight) < 4;
						if (pinnedToBottom) offset = rect.height;

						if (!this.rewardsResizeObserver && "ResizeObserver" in window) {
							this.rewardsResizeObserver = new ResizeObserver(() =>
								this.applyRewardsOffset()
							);
							this.rewardsResizeObserver.observe(banner);
						}
					}

					this.style.setProperty(
						"--sticky-atc-bottom",
						offset ? `${offset}px` : "0px"
					);
				};

				this.applyRewardsOffset();
				window.addEventListener("resize", this.applyRewardsOffset);

				// Poll briefly for the late-loading Yotpo script (stops once found).
				let tries = 0;
				const poll = () => {
					this.applyRewardsOffset();
					if (this.rewardsResizeObserver || tries++ > 20) return;
					window.setTimeout(poll, 500);
				};
				poll();
			}

			/* ---- Mobile "Select Size" -------------------------------------- */

			setupMobileSelect() {
				if (!this.stickySelect) return;

				this.stickySelect.addEventListener("click", () => {
					const picker =
						this.mainPicker ||
						document.querySelector(
							`#variant-radios-${this.sectionId}, #variant-selects-${this.sectionId}`
						);
					const target = picker
						? picker.closest(".product-parameters") || picker
						: null;
					if (!target) return;

					target.scrollIntoView({ behavior: "smooth", block: "center" });

					// Move focus to the first selectable option for keyboard users.
					const firstOption = target.querySelector(
						"input:not(.disabled), select, button"
					);
					if (firstOption) {
						window.setTimeout(
							() => firstOption.focus({ preventScroll: true }),
							420
						);
					}
				});
			}
		}
	);
}
